import { Button, Grid, List, ListItem, Theme, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../app/hooks/hooks';
import { getClaimNumber, getPartnerId, getServicingOpCo } from '../../../app/store/combinedSelectors';
import { updateStageState } from '../../../app/store/EligibilityService/EligibilityServiceSlice';
import { goToClaimsBtn } from '../../../Constants/Constants';
import { PostMessageCodes } from '../../../Constants/PostMessageCodes';
import { Features, IconStateType, OpCo, PartnerId } from '../../../types/type';
import { getNextStageInfo, getNextView, getPrettyClaimNumber, postMessageToParentApp } from '../../../utils/Utilities';
import CallToAction from '../../shared/CallToAction/CallToAction';
import ConfirmationHeader from '../../shared/ConfirmationHeader/ConfirmationHeader';
import ConfirmationNextSteps from '../../shared/ConfirmationNextSteps/ConfirmationNextSteps';
import ModalLinearActivityIndicator from '../../shared/ModalLinearActivityIndicator/ModalLinearActivityIndicator';
import StepProgressBar from '../../shared/StepProgressBar/StepProgressBar';

export default function PropertyConfirmation() {
  const theme: Theme = useTheme();
  const claimNumber = useSelector(getClaimNumber);
  const headerContent = 'Your claim has been submitted.';
  const subHeaderContent = 'Claim #: ' + getPrettyClaimNumber(claimNumber);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [modalLoadingOpen, setModalLoadingOpen] = React.useState(false);
  const availableProgressStages = useAppSelector((state) => state.services.availableProgressStages);
  const staticProgressStages = useAppSelector((state) => state.services.staticProgressStages);
  const claimSummary = useAppSelector((state) => state.claimDetail.claimSummary);
  const opco = useSelector(getServicingOpCo);
  const partnerId = useSelector(getPartnerId);

  // Get the rest of stages excluding the current stage. Find the next stage which is valid
  const nextStage = getNextStageInfo(Features.REPORT_STAGE, availableProgressStages);

  /* If there are eligible services, the total number of stages will be more than 2 (Report and Complete are in the stages collection by default) */
  const eligibleServicesLoaded = availableProgressStages.length > 2;

  function handleNext() {
    // Update the current stage state and prepare the next stage state.
    const updatedStages = availableProgressStages.map((stage) => {
      stage =
        stage.id === Features.REPORT_STAGE
          ? { ...stage, completed: true, viewActive: false, showProgress: true }
          : stage;
      stage =
        stage.id === nextStage.id
          ? {
              ...stage,
              completed: false,
              viewActive: true,
              showProgress: false,
            }
          : stage;
      return stage;
    });
    dispatch(updateStageState({ updatedStages: updatedStages }));
    navigate(getNextView(nextStage.id));
  }

  async function goToClaimsOverview() {
    if (partnerId?.toUpperCase() === PartnerId.AFICS) {
      setModalLoadingOpen(true);
      postMessageToParentApp(
        PostMessageCodes.DIGITAL_CLAIM_POST_FNOL_GO_TO_CLAIMS,
        undefined,
        undefined,
        claimSummary?.policyNumber
      );
    } else if (opco === OpCo.HOMESITE || opco === OpCo.AMFAM) {
      navigate('/claims-overview', {
        state: {
          isClaimRefreshNeeded: true,
        },
      });
    } else {
      navigate('/claim-detail');
    }
  }

  const introContent = (
    <>
      <Grid item xs={12}>
        <Typography sx={{ marginTop: '20px' }} variant="body1">
          We've sent you a confirmation email for your records.
        </Typography>
      </Grid>
      <Grid item xs={12}>
        {!eligibleServicesLoaded && (
          <Typography sx={{ marginTop: '20px' }} variant="body1">
            You will receive further communication on your claim as it progresses. We cannot confirm or deny coverage
            for your claim at this time. Please take reasonable steps to secure your property and prevent further
            damage.
          </Typography>
        )}
        {eligibleServicesLoaded && (
          <Typography sx={{ marginTop: '20px' }} variant="body1">
            You will receive further communication on your claim as it progresses. We cannot confirm or deny coverage
            for your claim at this time. If you select services available to you, please be fully aware that you may be
            responsible for charges in the event coverage does not apply for your loss. Please take reasonable steps to
            secure your property and prevent further damage.
          </Typography>
        )}
      </Grid>
    </>
  );

  const inAdditionContent = (
    <>
      <Grid item xs={12}>
        <Typography sx={{ marginTop: '20px' }} variant="body1">
          <b>In addition, please: </b>
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <List sx={theme.custom.styles.confirmationList}>
          <ListItem sx={theme.custom.styles.confirmationListElement}>
            <Typography variant="body1">Take pictures of all affected items and areas.</Typography>
          </ListItem>
          <ListItem sx={theme.custom.styles.confirmationListElement}>
            <Typography variant="body1">
              Keep all related receipts and paperwork from contractors and repairs for your adjuster to review.
            </Typography>
          </ListItem>

          <ListItem sx={theme.custom.styles.confirmationListElement}>
            <Typography variant="body1">
              Keep any damaged items (sump pump, broken pipe, hose, etc.) for the adjuster to view.
            </Typography>
          </ListItem>

          <ListItem sx={theme.custom.styles.confirmationListElement}>
            <Typography variant="body1">
              If any of the items pose a health threat, please document and handle accordingly.
            </Typography>
          </ListItem>

          <ListItem sx={theme.custom.styles.confirmationListElement}>
            <Typography variant="body1">
              If applicable, keep a 12x12-inch swatch of any affected carpet in a plastic bag.
            </Typography>
          </ListItem>

          <ListItem sx={theme.custom.styles.confirmationListElement}>
            <Typography variant="body1">You can monitor the status of your claim online.</Typography>
          </ListItem>
        </List>
      </Grid>
    </>
  );

  /* If there are eligible services, the total number of stages will be more than 2 (Report and Complete are in the stages collection by default) */
  const content = (
    <>
      {eligibleServicesLoaded && <StepProgressBar stateInfo={availableProgressStages}></StepProgressBar>}
      {!eligibleServicesLoaded && <StepProgressBar stateInfo={staticProgressStages}></StepProgressBar>}
      <Grid
        container
        sx={{
          width: '100%',
          maxWidth: '1280px',
          minWidth: '310px',
          margin: '0 auto',
        }}
      >
        <Grid item xs={12}>
          <ConfirmationHeader
            headerContent={headerContent}
            subHeaderContent={subHeaderContent}
            state={IconStateType.SUCCESS_STATE}
          ></ConfirmationHeader>
        </Grid>

        {!eligibleServicesLoaded && (
          <Grid item xs={12}>
            <Typography
              sx={{
                ...theme.custom.styles.serviceConfirmationHeader,
                marginTop: '0rem',
              }}
            >
              Next Steps
            </Typography>
          </Grid>
        )}
        {introContent}
        {eligibleServicesLoaded && (
          <>
            <Grid item xs={12}>
              <Typography
                sx={{
                  ...theme.custom.styles.serviceConfirmationHeader,
                  marginTop: '0rem',
                }}
              >
                Next Steps
              </Typography>
            </Grid>
            <ConfirmationNextSteps nextStep={nextStage.name} stages={availableProgressStages}></ConfirmationNextSteps>
          </>
        )}
        {inAdditionContent}
      </Grid>

      <Grid
        container
        sx={{
          marginTop: '3em',
          width: '100%',
          maxWidth: '1280px',
          minWidth: '310px',
          margin: '0 auto',
        }}
      >
        <Grid item xs={12}>
          {!eligibleServicesLoaded && (
            <Grid item xs={12}>
              <Button
                sx={{
                  ...theme.custom.styles.ctaNextButton,
                  '&.MuiButtonBase-root:hover': {
                    bgcolor: theme.custom.colors.primaryButtonHoverColor,
                  },
                }}
                onClick={goToClaimsOverview}
              >
                {goToClaimsBtn}
              </Button>
            </Grid>
          )}
          {eligibleServicesLoaded && (
            <CallToAction
              nextButton={true}
              nextButtonContent={'Continue to ' + nextStage.name.toLowerCase()}
              handleNext={handleNext}
              skipButton={false}
              backButton={false}
              cancelButton={true}
              cancelButtonContent={goToClaimsBtn}
              handleCancel={goToClaimsOverview}
            ></CallToAction>
          )}
        </Grid>
      </Grid>
    </>
  );
  return (
    <Box sx={{ width: '100%' }}>
      {content}
      {modalLoadingOpen && <ModalLinearActivityIndicator />}
    </Box>
  );
}
